import { z } from 'zod';
import { BasePatientSchema, type BasePatient } from '@/api/schema/basePatient';
import { ProtocolSchema } from '@/api/schema/protocol';
import { EligibilityStatusSchema, PhaseSchema, type Phase } from '@/api/schema/eligibilityStatus';

export const SubstanceSubtypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  ratio: z.string(),
});

export const StudyProfileSubstanceSchema = z.object({
  id: z.string(),
  name: z.string(),
  subtypes: z.array(SubstanceSubtypeSchema),
});

export const StudyProfileSchema = z.object({
  id: z.string(),
  substances: z.array(StudyProfileSubstanceSchema),
  excludeIvImages: z.boolean(),
});

export const STATUS = {
  ELIGIBLE: 'Eligible',
  NOT_ELIGIBLE: 'Not eligible',
  POST_PROCEDURE: 'Post procedure',
  VERIFICATION: 'Verification',
  SCREENING: 'Screening',
} as const;

export const SubjectPhaseSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const SubjectProtocolSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.nativeEnum(STATUS),
  phase: SubjectPhaseSchema,
});

export const SubjectProtocolWithStudyProfileSchema = SubjectProtocolSchema.extend({
  studyProfile: z.object({
    excludeIvImages: z.boolean(),
    substances: z.array(z.object({
      substance: z.string(),
      subtypes: z.array(z.object({
        subtype: z.string(),
        ratio: z.number(),
      })),
    })),
  }),
  consents: z.object({
    sent: z.array(z.unknown()),
    pending: z.array(z.object({
      id: z.string(),
      externalId: z.string().nullable(),
      status: z.string(),
      signedAt: z.string().optional(),
      template: z.object({
        id: z.string(),
        externalId: z.string(),
        name: z.string(),
      }).nullable(),
      confirmedBy: z.object({
        id: z.string(),
        email: z.string(),
        name: z.string(),
        role: z.string(),
      }).nullable(),
    })),
  }),
});

export const SubjectSchema = z.object({
  id: z.string(),
  subjectHumanReadableId: z.string(),
  isActive: z.boolean(),
  protocol: SubjectProtocolSchema,
});

export const AnonymizedBasePatientSchema = z.object({
  id: z.string(),
  humanReadableId: z.string(),
  externalId: z.string(),
  dateOfBirth: z.string(),
  gender: z.string(),
  subjects: z.array(SubjectSchema).optional(),
  site: z.object({
    id: z.string(),
  }),
});

export const BasePatientUnionSchema = z.union([ BasePatientSchema, AnonymizedBasePatientSchema ]);

export const SubjectDetailsSchema = z.object({
  id: z.string(),
  humanReadableId: z.string(),
  patient: BasePatientUnionSchema,
  isActive: z.boolean(),
  eligibilityStatus: EligibilityStatusSchema,
  currentPhase: PhaseSchema,
  studyProfile: StudyProfileSchema,
  isCueingStarted: z.boolean(),
});

export const ProtocolListSchema = z.object({
  protocols: z.array(ProtocolSchema),
});

export const SubtypeSchema = z.object({
  subtype: z.string(),
});

export const SubstanceSchema = z.object({
  substance: z.string(),
  color: z.string(),
  subtypes: z.array(z.string()),
  isNeutral: z.boolean().optional(),
});

export const SubstancesListSchema = z.object({
  substances: z.array(SubstanceSchema),
});

export const ProtocolPhasesListSchema = z.array(SubjectPhaseSchema);

export const CreateSubjectSchema = z.object({
  patientId: z.string(),
  protocolId: z.string(),
  excludeIvImages: z.boolean(),
  substances: z.array(z.object({
    substance: z.string(),
    subtypes: z.array(z.object({
      subtype: z.string(),
      ratio: z.number(),
    })).nullable(),
  })),
});

export const UpdateSubjectSchema = z.object({
  phaseId: z.string().optional(),
  excludeIvImages: z.boolean().optional(),
  substances: z.array(z.object({
    substance: z.string(),
    subtypes: z.array(z.object({
      subtype: z.string(),
      ratio: z.number(),
    })).nullable(),
  })).optional(),
});

export type Patient = BasePatient;
export type CurrentPhase = Phase;
export type SubstanceSubtype = z.infer<typeof SubstanceSubtypeSchema>;
export type StudyProfileSubstance = z.infer<typeof StudyProfileSubstanceSchema>;
export type StudyProfile = z.infer<typeof StudyProfileSchema>;
export type Status = typeof STATUS[keyof typeof STATUS];
export type SubjectPhase = z.infer<typeof SubjectPhaseSchema>;
export type SubjectProtocol = z.infer<typeof SubjectProtocolSchema>;
export type SubjectProtocolWithStudyProfile = z.infer<typeof SubjectProtocolWithStudyProfileSchema>;
export type Subject = z.infer<typeof SubjectSchema>;
export type SubjectDetails = z.infer<typeof SubjectDetailsSchema>;
export type Protocol = z.infer<typeof ProtocolSchema>;
export type ProtocolList = z.infer<typeof ProtocolListSchema>;
export type Subtype = z.infer<typeof SubtypeSchema>;
export type Substance = z.infer<typeof SubstanceSchema>;
export type SubstancesList = z.infer<typeof SubstancesListSchema>;
export type ProtocolPhasesList = z.infer<typeof ProtocolPhasesListSchema>;
export type CreateSubject = z.infer<typeof CreateSubjectSchema>;
export type UpdateSubject = z.infer<typeof UpdateSubjectSchema>;
